import { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Stage, Layer, Text, Group, Image as KImage } from 'react-konva'
import { Html } from 'react-konva-utils'
import {
  pipe,
  pathOr,
  find,
  propEq,
  path,
  findIndex,
  sort,
  descend,
  prop,
  length,
  equals,
  values,
  filter,
  map,
  reduce
} from 'ramda'
import { v4 as uuidv4 } from 'uuid'
import styled from 'styled-components'
import { camelCase, noop, isEmpty } from 'lodash'
import { useLocation } from 'react-router-dom'
import moment from 'moment'
import Icon from '@/components/icon'
import { colors } from '@/styles'
import useIsMobile from '@/hooks/use-is-mobile'
import { breakpoints } from '@/styles'
import { LoadingSpinner } from '@/components/loading-circle'
import ContractImage from './contract-image'
import PageButtons from './page-buttons'
import SigningProgressBar from './signing-progress-bar'
import CreateContractButton from './create-contract-button'
import DocumentWidget from '../document-widget'
import DocumentPageWidget from '../document-page-widget'
import InputWidget from '../input-widget'
import Context from '../context'
import { showInitialModal, showSignatureModal } from '../modal'
import { updateAt, displayExistingItems } from '../utils'

const CounterWrapper = styled.div`
  display: inline-block;
  text-align: center;
  font-size: 14px;
  width: 52px;
  height: 32px;
  line-height: 32px;
  border: 1px solid ${colors.grey88};
  user-select: none;
  background-color: ${colors.white};
  color: ${colors.grey56};
`

const Chevron = styled.div`
  display: inline-block;
  text-align: center;
  font-size: 14px;
  width: 32px;
  height: 32px;
  line-height: 32px;
  border: 1px solid ${colors.grey88};

  user-select: none;
  ${props =>
    !props.disabled &&
    `
      cursor: pointer;
      &: hover{
        background-color: ${colors.grey96};
      }

      &: active {
        background-color: ${colors.blue16};
        color: ${colors.white};
      }
  `}

  background-color: ${props => (props.disabled ? colors.grey96 : colors.white)};

  > svg {
    margin-bottom: 1px;
  }
`

const LeftChevron = styled(Chevron)`
  border-radius: 6px 0px 0px 6px;
  border-right: 0;
`

const RightChevron = styled(Chevron)`
  border-radius: 0px 6px 6px 0px;
  border-left: 0;
`

const Container = styled.div`
  display: grid;
  grid-template-columns: ${props => (props.isMobile ? '1fr' : '1fr 2fr 1fr')};
  width: 100%;
  height: 100%;
  margin-bottom: 94px;
`

const CustomContractWrapper = styled.div`
  grid-column-start: 2;
  grid-column-end: 3;
  ${props => (props.isMobile ? 'grid-row:2' : '')};
  display: flex;
  flex-flow: wrap;
  ${props => (!props.isSigning ? `flex-direction: column-reverse` : `flex-direction: column`)};
`

const CustomContractViewWrapper = styled.div`
  order: 2;
`

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  ${props => props.width && `width: ${props.width}px;`}
  ${props => props.height && `height: ${props.height}px;`}
`

const CustomContractControllerWrapper = styled.div`
  order: 1;
  margin: 10px auto;
  ${props => !props.isMobile && 'margin-top: 20px'};
`

const DocumentNameWrapper = styled.div`
  color: ${colors.grey48};
  max-width: 500px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    max-width: 250px;
    padding-left: 24px;
  }
`

const DocumentSettingsWrapper = styled.div`
  grid-column-start: ${props => (props.isMobile ? '1' : '3')};
  padding: 10px;
`
const DocumentsChangeWrapper = styled.div`
  grid-column-start: '1';
  padding: 10px;
`

function getPdfValue(displayRatio, resolution, value) {
  return (value / displayRatio / resolution) * 25.4
}

function getPdfTextValue(value) {
  return value * 0.75
}

const calculateAspectRatioFit = (srcWidth, srcHeight, maxWidth, maxHeight) => {
  const ratio = Math.max(maxWidth / srcWidth, maxHeight / srcHeight)
  return { width: Math.round(srcWidth * ratio), height: Math.round(srcHeight * ratio) }
}

const loadImage = (setImageDimensions, imageUrl, STAGE_WIDTH, STAGE_HEIGHT) => {
  const img = new Image()
  img.src = imageUrl

  img.onload = () => {
    const resizedImage = calculateAspectRatioFit(img.width, img.height, STAGE_WIDTH, STAGE_HEIGHT)
    setImageDimensions({
      height: img.height,
      width: img.width,
      resizedHeight: resizedImage.height,
      resizedWidth: resizedImage.width
    })
  }
  img.onerror = err => {
    console.log('img error')
    console.error(err)
  }
}

const DocumentView = ({
  currentStep,
  contractId,
  signees,
  setSignees,
  pages,
  setPages,
  signingFinished = false,
  selectedPage,
  setSelectedPage,
  isEdit = false,
  handleBack,
  sendSignatures = noop,
  handleNextField = noop,
  setIsFilled,
  setSigningFinished = noop,
  hasSigned = false,
  isPackageFileSigned = false,
  currentSigner = {}
}) => {
  const [imageDimensions, setImageDimensions] = useState({})
  const [zoomLevel, setZoomLevel] = useState(100)
  const [itemsLength, setItemsLength] = useState(0)
  const [existingSignatures, setExistingSignatures] = useState(null)
  const [existingTextFields, setExistingTextFields] = useState(null)
  const isMobile = useIsMobile()
  const isCreateContract = currentStep === 'createContract'
  const isBuildDocuments = currentStep === 'buildDocuments'
  const MOBILE_WIDTH = window.screen?.width || 375
  const MOBILE_HEIGHT = 586
  const DESKTOP_WIDTH = 812
  const DESKTOP_HEIGHT = 1041
  const STAGE_WIDTH = isMobile ? MOBILE_WIDTH : DESKTOP_WIDTH
  const STAGE_HEIGHT = isMobile ? MOBILE_HEIGHT : DESKTOP_HEIGHT
  const VIEWPORT_HEIGHT = isMobile ? MOBILE_HEIGHT : DESKTOP_HEIGHT
  const stageRef = useRef()
  const [stage, setStage] = useState({
    scale: 1,
    x: 0,
    y: 0
  })
  const userContext = useContext(Context)
  const {
    items,
    setItems,
    fileMetadata,
    setFileMetadata,
    signedItems,
    setSignedItems,
    bottomNavSelectedItem,
    selectedShape,
    selectShape
  } = userContext

  const currentDocumentId = fileMetadata?.current_document

  const currentDocument = pipe(
    pathOr([], ['package_files']),
    find(propEq('file_id', currentDocumentId))
  )(fileMetadata)

  const formattedFiles = pipe(
    pathOr([], ['package_files']),
    sort(descend(prop('document_type')))
  )(fileMetadata)

  const currentIndex = pipe(findIndex(propEq('file_id', currentDocumentId)))(formattedFiles)
  const isLastPage = pipe(values, length, equals(currentIndex + 1))(pages)

  const currentItems = pathOr([], [currentDocumentId], items)

  const location = useLocation()
  const dispatch = useDispatch()
  const isSigning = location.pathname.includes('/custom-contract/sign')

  const displayExisting = currentDocument?.fields_metadata?.pages && isBuildDocuments && isEdit

  const textFieldDisplayColor = '#000000'
  const nonEditType = ['initials', 'signature', 'dateSigned']
  const progressStart = signedItems?.taps?.length !== undefined ? signedItems?.taps?.length : 0

  const taps = fileMetadata?.taps ?? []
  const mobileWidth = MOBILE_WIDTH / DESKTOP_WIDTH
  const mobileHeight = MOBILE_HEIGHT / DESKTOP_HEIGHT
  const urls = currentDocument?.location[0] ?? []

  const updateCoordinates = item => {
    return {
      x: isMobile
        ? (item.metadata.konva_coordinates.x / DESKTOP_WIDTH) * MOBILE_WIDTH
        : item.metadata.konva_coordinates.x,
      y: isMobile
        ? (item.metadata.konva_coordinates.y / DESKTOP_HEIGHT) * MOBILE_HEIGHT
        : item.metadata.konva_coordinates.y,
      width: isMobile
        ? item.metadata.konva_coordinates.width * mobileWidth
        : item.metadata.konva_coordinates.width,
      height: isMobile
        ? item.metadata.konva_coordinates.height * mobileHeight
        : item.metadata.konva_coordinates.height,
      stroke: item.metadata.konva_coordinates.color
    }
  }

  useEffect(() => {
    window.addEventListener('click', onClickHandler)
    return () => window.removeEventListener('click', onClickHandler)
  }, [])

  useEffect(() => {
    if (!isSigning) return
    const existingTaps = async taps => {
      try {
        const promises = taps?.map(async item => {
          return await dispatch.contract.customContractGetSignatures({
            contractId: contractId,
            tapId: item?.tapId
          })
        })
        return await Promise.all(promises)
      } catch (error) {
        console.log('error', error)
      }
    }

    existingTaps(taps)
      .then(items => {
        const objects = {}
        items?.map(item => {
          if (item.body.taps.signer_id in objects) {
            if (objects[item.body.taps.signer_id].value?.initial === null)
              objects[item.body.taps.signer_id].value.initial = item.body.initial
            if (objects[item.body.taps.signer_id].value?.signature === null)
              objects[item.body.taps.signer_id].value.signature = item.body.signature
            objects[item.body.taps.signer_id].value.taps = [
              ...objects[item.body.taps.signer_id].value.taps,
              item.body.taps
            ]
          } else {
            objects[item.body.taps.signer_id] = {
              value: {
                initial: item.body.initial,
                signature: item.body.signature,
                taps: [item.body.taps]
              }
            }
          }
        })
        setExistingSignatures(objects)
      })
      .catch(error => console.log(error))
  }, [fileMetadata?.taps])

  useEffect(() => {
    const stage = stageRef?.current?.getStage()
    const layer = stage?.find('Layer')
    layer[0]?.find('Group').forEach(item => {
      if (item.name() !== `page-${selectedPage}`) item.hide()
    })
    // Unsafe missing useEffect deps - TODO refactor
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingSignatures])

  useEffect(() => {
    if (isBuildDocuments) setIsFilled(true)
  }, [isBuildDocuments])

  useEffect(() => {
    if (currentItems.length > itemsLength) setItemsLength(currentItems.length)
  }, [currentItems.length, itemsLength])

  useEffect(() => {
    if (!displayExisting) return

    const formattedItems = pipe(
      reduce((acc, file) => {
        const currentSignees = signees?.[file?.id]
        const existingItems = displayExistingItems(file?.fields_metadata?.pages, currentSignees)

        return {
          ...acc,
          [file?.id]: existingItems
        }
      }, {})
    )(formattedFiles)

    setItems(formattedItems)
  }, [])

  // TODO: clean the logic below
  useEffect(() => {
    if (!isSigning) return
    let signeeItems = []
    let existingFields = []

    if (!currentDocument) {
      return
    }
    if (isEmpty(currentDocument?.fields_metadata?.pages)) {
      setSigningFinished(true)
      return
    }
    for (const [key, value] of Object.entries(currentDocument?.fields_metadata?.pages)) {
      for (const [nestedKey, nestedValue] of Object.entries(value)) {
        if (nestedValue.type === 'text') {
          existingFields.push({
            ...updateCoordinates(nestedValue),
            name: `page-${key}`,
            id: nestedValue.itemId,
            text: nestedValue.metadata.konva_display_text,
            inputType: nestedValue.type,
            textSize: nestedValue.metadata.konva_display_text_size
          })
        }
        if (fileMetadata?.current_signer_id === nestedValue?.details.signer_id) {
          if (nestedValue.type === 'dateSigned') {
            existingFields.push({
              ...updateCoordinates(nestedValue),
              name: `page-${key}`,
              id: nestedValue.itemId,
              text: moment(new Date()).format('MM/DD/YY'),
              inputType: nestedValue.type,
              textSize: nestedValue.metadata.konva_display_text_size
            })
          }
          if (nestedValue.type !== 'dateSigned' && nestedValue.type !== 'text') {
            signeeItems.push({
              ...updateCoordinates(nestedValue),
              name: `page-${key}`,
              id: nestedValue.itemId,
              text: `${nestedValue.type.charAt(0).toUpperCase() + nestedValue.type.slice(1)} field`,
              inputType: nestedValue.type,
              textSize: nestedValue.metadata.konva_display_text_size
            })
          }
        }
      }
    }
    const updatedItems = updateAt(items, [currentDocumentId], signeeItems)
    setExistingTextFields(updateAt([], [currentDocumentId], existingFields))
    setItems(updatedItems)
    if (length(signeeItems) === 0) setSigningFinished(true)
    selectShape(signeeItems[0]?.id)
  }, [currentDocument?.fields_metadata?.pages, isSigning, setItems])

  useEffect(() => {
    const stage = stageRef?.current?.getStage()
    const layer = stage.find('Layer')

    if (isCreateContract) {
      handleCanvasPageEvent(selectedPage, 0)
      setSelectedPage(0)

      let fields = []
      let groupedFields

      layer[0].find('Group').forEach(item => {
        if (item.id().includes('label')) {
          const [userId, fieldName] = item.id().split('_')

          const signerId = signees?.[currentDocumentId][userId]?.['signer_id']

          const parsedFieldName = fieldName.replace('Field', '')
          const page = item.name().split('-')[1]
          let fieldDisplayName
          let fieldTextSize
          item.find('Text').forEach(textItem => {
            fieldDisplayName = textItem.attrs.text
            fieldTextSize = textItem.attrs.fontSize
          })
          const displayRatio = (STAGE_WIDTH / imageDimensions.width) * 10
          const resolution = currentDocument.location[1][0].x
          fields.push({
            [page]: [
              {
                itemId: uuidv4(),
                page: page,
                x: getPdfValue(displayRatio, resolution, item.x()),
                y: getPdfValue(displayRatio, resolution, item.y()),
                width: getPdfValue(displayRatio, resolution, item.width()),
                height: getPdfValue(displayRatio, resolution, item.height()),
                type: nonEditType.includes(parsedFieldName) ? parsedFieldName : 'text',
                metadata: {
                  konva_id: item.id(),
                  konva_coordinates: {
                    x: item.x(),
                    y: item.y(),
                    width: item.width(),
                    height: item.height(),
                    color: item.attrs.color
                  },
                  konva_display_text: fieldDisplayName,
                  konva_display_text_size: fieldTextSize,
                  konva_pdf_text_size: getPdfTextValue(fieldTextSize)
                },
                details:
                  parsedFieldName === 'dateSigned'
                    ? {
                        signer_id: signerId,
                        format: 'Y-m-d'
                      }
                    : {
                        signer_id: signerId
                      }
              }
            ]
          })
        }
      })
      groupedFields = fields.reduce((acc, curr) => {
        const key = Object.keys(curr)[0]
        const found = acc.find(i => i[key])
        if (!found) {
          acc.push(curr)
        } else {
          found[key] = [...found[key], ...curr[key]]
        }
        return acc
      }, [])

      setPages(updateAt(pages, [currentDocumentId], Object.assign({}, ...groupedFields)))
      selectShape(null)

      // Turn text fields to black
      const newItems = currentItems.map(item => {
        if (item.inputType === 'text') {
          return {
            ...item,
            stroke: textFieldDisplayColor
          }
        }
        return item
      })

      const updatedItems = updateAt(items, [currentDocumentId], newItems)
      setItems(updatedItems)
    }
    // Unsafe missing useEffect deps - TODO refactor
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, currentDocumentId])

  useEffect(() => {
    if (urls?.length > 0) loadImage(setImageDimensions, urls[0], STAGE_WIDTH, STAGE_HEIGHT)

    // Unsafe missing useEffect deps - TODO refactor
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urls])

  useEffect(() => {
    const stage = stageRef?.current?.getStage()
    const layer = stage.find('Layer')
    layer[0]?.find('Group').forEach(item => {
      item.hide()
      if (item.name() === `page-${selectedPage}`) item.show()
    })
    // Unsafe missing useEffect deps - TODO refactor
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])

  useEffect(() => {
    if (!isSigning) return
    const stage = stageRef?.current?.getStage()
    const layer = stage?.find('Layer')
    const remainingItems = !Array.isArray(items) || !items.length

    if (selectedPage === 0) {
      layer[0]?.find('Group').forEach(item => {
        // Hack to display transformer box
        if (item.id() === '') item.show()
      })
    }

    const selectedItemPage = items[0]?.name?.split('-')[1]

    selectShape(items[0]?.id)

    if (signedItems?.taps?.length > 0 && !remainingItems) {
      handleCanvasPageEvent(selectedPage, selectedItemPage)
      setSelectedPage(parseInt(selectedItemPage))
    }
  }, [items])

  useEffect(() => {
    if (bottomNavSelectedItem === undefined) return
    const isEmpty =
      Object.keys(bottomNavSelectedItem).length === 0 &&
      bottomNavSelectedItem.constructor === Object
    if (isEmpty) return
    const stage = stageRef?.current?.getStage()
    const layer = stage?.find('Layer')
    const selectedItemPage = bottomNavSelectedItem?.name?.split('-')[1]
    selectShape(
      currentItems[currentItems.findIndex(item => item.id === bottomNavSelectedItem.id)]?.id
    )

    if (selectedItemPage != selectedPage) {
      handleCanvasPageEvent(selectedPage, selectedItemPage)
      setSelectedPage(parseInt(selectedItemPage))
    }
  }, [bottomNavSelectedItem])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [selectedPage])

  const handlePageChange = direction => {
    const stage = stageRef?.current?.getStage()
    const layer = stage?.find('Layer')
    const selectedItemPage = direction === 'left' ? selectedPage + 1 : selectedPage - 1
    const currentPageItem = currentItems.filter(
      item => parseInt(item.name.split('-')[1]) === selectedItemPage
    )

    handleCanvasPageEvent(selectedPage, selectedItemPage)

    updateSelectedPage(selectedItemPage)
    if (isSigning) selectShape(currentPageItem[0]?.id)
  }

  const handleCanvasPageEvent = (selectedPage, selectedItemPage) => {
    const stage = stageRef?.current?.getStage()
    const layer = stage?.find('Layer')

    function handleOpacity(item) {
      if (item.name() === `page-${selectedPage}`) item?.hide()
      if (item.name() === `page-${selectedItemPage}`) item?.show()
    }
    layer[0].find(`#page-${selectedPage}`)[0]?.hide()
    layer[0].find(`#page-${selectedItemPage}`)[0]?.show()
    layer[0].find('Group').forEach(item => {
      handleOpacity(item)
    })
    layer[0].find('Image').forEach(item => {
      handleOpacity(item)
    })
    layer[0].find('Text').forEach(item => {
      handleOpacity(item)
    })
  }
  const loadNewUrlImage = image => loadImage(setImageDimensions, image, STAGE_WIDTH, STAGE_HEIGHT)

  const selectPage = direction => {
    selectShape(null)
    if (isSigning) {
      if (direction === 'left') {
        if (selectedPage + 1 <= urls.length - 1) {
          handlePageChange(direction)
        }
      } else {
        if (selectedPage - 1 >= 0) {
          handlePageChange(direction)
        }
      }
    } else {
      if (direction === 'left') {
        if (selectedPage + 1 <= urls.length - 1) {
          handlePageChange(direction)
        } else {
          const nextId = formattedFiles?.[currentIndex + 1]?.id
          const nextUrls = pipe(
            pathOr([], ['package_files']),
            find(propEq('file_id', nextId)),
            pathOr([], ['location', 0])
          )(fileMetadata)
          setFileMetadata({ ...fileMetadata, current_document: nextId })
          handleCanvasPageEvent(selectedPage, 0)
          updateSelectedPage(0)
          loadNewUrlImage(nextUrls[0])
        }
      } else {
        if (selectedPage - 1 >= 0) {
          handlePageChange(direction)
        } else {
          const previousId = formattedFiles?.[currentIndex - 1]?.id
          const previousUrls = pipe(
            pathOr([], ['package_files']),
            find(propEq('file_id', previousId)),
            pathOr([], ['location', 0])
          )(fileMetadata)
          setFileMetadata({ ...fileMetadata, current_document: previousId })
          handleCanvasPageEvent(selectedPage, 0)
          updateSelectedPage(0)
          loadNewUrlImage(previousUrls[0])
        }
      }
    }
  }

  const handleDuplicate = e => {
    const item = currentItems.filter(obj => obj.id === selectedShape)
    const type = item[0].inputType
    const newId = `${selectedShape.split('_')[0]}-${camelCase(item[0].text)}_${Math.floor(
      Math.random() * 10000
    )}`

    setItems(
      updateAt(
        items,
        [currentDocumentId],
        [
          ...currentItems,
          {
            x: item[0].x + 10,
            y: item[0].y + 10,
            width: item[0].width,
            height: item[0].height,
            stroke: item[0].stroke,
            name: item[0].name,
            id: newId,
            text: item[0].text,
            inputType: nonEditType.includes(type) ? type : 'text',
            textSize: item[0].textSize
          }
        ]
      )
    )
    selectShape(newId)
  }

  const handleDelete = () => {
    const newItems = currentItems.filter(obj => obj.id !== selectedShape)
    setItems(updateAt(items, [currentDocumentId], [...newItems]))
    selectShape(null)
  }

  const handleZoom = direction => {
    const scaleBy = 1.1
    const stage = stageRef?.current?.getStage()
    stage.setPointersPositions()
    const oldScale = stage.scaleX()

    let center = {
      x: stage.width() / 2,
      y: stage.height() / 2
    }

    let newScale = direction === 'up' ? oldScale * scaleBy : oldScale / scaleBy

    if (newScale > 2) {
      newScale = 2
    } else if (newScale < 1) {
      newScale = 1
    }

    stage.width(imageDimensions.resizedWidth * newScale)
    stage.height(imageDimensions.resizedHeight * newScale)

    stage.scale({
      x: newScale,
      y: newScale
    })

    stage.batchDraw()

    if (newScale === 2) {
      setZoomLevel(200)
    } else if (newScale === 1) {
      setZoomLevel(100)
    } else {
      setZoomLevel(
        direction === 'up' ? Math.round(zoomLevel * scaleBy) : Math.round(zoomLevel / scaleBy)
      )
    }
  }

  function getDistance(p1, p2) {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2))
  }

  function getCenter(p1, p2) {
    return {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2
    }
  }

  let lastCenter = null
  let lastDist = 0

  function handleTouch(e) {
    // e.evt.preventDefault()
    const touch1 = e.evt.touches[0]
    const touch2 = e.evt.touches[1]
    const stage = stageRef.current

    if (stage !== null) {
      if (touch1 && touch2) {
        // isDragging is not working
        stage.stopDrag()
        // if (stage.isDragging()) {
        //   stage.stopDrag()
        // }
        e.evt.preventDefault(true)

        const p1 = {
          x: touch1.clientX,
          y: touch1.clientY
        }
        const p2 = {
          x: touch2.clientX,
          y: touch2.clientY
        }

        if (!lastCenter) {
          lastCenter = getCenter(p1, p2)
          return
        }
        const newCenter = getCenter(p1, p2)

        const dist = getDistance(p1, p2)

        if (!lastDist) {
          lastDist = dist
        }

        // local coordinates of center point
        const pointTo = {
          x: (newCenter.x - stage.x()) / stage.scaleX(),
          y: (newCenter.y - stage.y()) / stage.scaleX()
        }

        let scale = stage.scaleX() * (dist / lastDist)

        // prevent scaling to less than 100%
        if (scale < 1) scale = 1

        stage.scaleX(scale)
        stage.scaleY(scale)

        // calculate new position of the stage
        const dx = newCenter.x - lastCenter.x
        const dy = newCenter.y - lastCenter.y

        const newPos = {
          x: newCenter.x - pointTo.x * scale + dx,
          y: newCenter.y - pointTo.y * scale + dy
        }

        stage.position(newPos)
        stage.batchDraw()

        lastDist = dist
        lastCenter = newCenter
      }
    }
  }

  function handleTouchEnd() {
    const stage = stageRef.current
    lastCenter = null
    lastDist = 0

    if (stage.scaleX() === 1) {
      stage.position({ x: 0, y: 0 })
      stage.batchDraw()
    }
  }

  const updateSelectedPage = page => {
    setSelectedPage(page)
    selectShape(null)
  }

  const onClickHandler = e => {
    // Using try catch to suppress error
    // TODO: Refactor
    try {
      if (e.target.attrs.name === 'image' || isCreateContract) {
        selectShape(null)
      }
      if (!!e.target.closest('.nonCanvas')) selectShape(null)
    } catch (err) {
      // Suppress error until we find a workaround
      // console.log(err)
    }
  }

  const displayTextFields = (source, selectedPage) => {
    let results = []

    pipe(
      pathOr([], [currentDocumentId]),
      filter(item => parseInt(item.name.split('-')[1]) === selectedPage),
      map((item, i) => {
        results.push(
          <Text
            key={item.id}
            id={item.name}
            name={item.name}
            text={item.text}
            fontSize={item.textSize}
            x={item.x}
            y={item.y}
            visible={true}
          />
        )
      })
    )(source)

    return results
  }

  const displaySignatures = source => {
    let results = []
    source.taps.map((item, i) => {
      const image = new Image()
      item.type === 'Initials' ? (image.src = source.initial) : (image.src = source.signature)

      results.push(
        <Group name={item.name} key={i}>
          <KImage
            key={i}
            id={`page-${i}`}
            name={item.name}
            image={image}
            width={item.width}
            height={item.height}
            x={item.x}
            y={item.y}
            visible={true}
          />
        </Group>
      )
    })
    return results
  }

  const nextPageDisabled = selectedPage + 1 >= urls.length
  const prevPageDisabled = selectedPage + 1 <= 1

  const handleSubmit = () => {
    const displayRatio = (STAGE_WIDTH / imageDimensions.width) * 10
    const resolution = currentDocument.location[1][0].x

    const dateFields = pipe(
      pathOr([], [currentDocumentId]),
      filter(propEq('inputType', 'dateSigned')),
      map(field => {
        return {
          name: field.name,
          itemId: field.id,
          date: moment(new Date()).format('YYYY-MM-DD HH:mm:ss'),
          type: field.inputType,
          height: field.height,
          stroke: field.stroke,
          x: field.x,
          y: field.y,
          textSize: field.textSize,
          pdf_width: getPdfValue(displayRatio, resolution, field.width),
          pdf_height: getPdfValue(displayRatio, resolution, field.height),
          pdf_x: getPdfValue(displayRatio, resolution, field.x),
          pdf_y: getPdfValue(displayRatio, resolution, field.y),
          signer_id: parseInt(fileMetadata.current_signer_id)
        }
      })
    )(existingTextFields)

    sendSignatures(dateFields)
  }

  return (
    <>
      {isSigning && (
        <SigningProgressBar
          progressStart={progressStart}
          itemsLength={itemsLength}
          signingFinished={signingFinished}
          handleSubmit={handleSubmit}
          handleNextField={handleNextField}
          hasSigned={hasSigned}
          isPackageFileSigned={isPackageFileSigned}
          contractId={contractId}
          currentDocumentId={currentDocumentId}
          signees={signees}
          currentSigner={currentSigner}
        />
      )}
      <Container isMobile={isMobile}>
        {!isSigning && (
          <DocumentsChangeWrapper className="nonCanvas" isMobile={isMobile}>
            <DocumentPageWidget
              urls={urls}
              stageRef={stageRef}
              selectedPage={selectedPage}
              setSelectedPage={setSelectedPage}
              updateSelectedPage={updateSelectedPage}
              zoomLevel={zoomLevel}
              handleClick={handleZoom}
              handleDuplicate={handleDuplicate}
              handleDelete={handleDelete}
              selectedShape={selectedShape}
              currentPage={currentStep}
              signees={signees}
              setSignees={setSignees}
              contractId={contractId}
              currentDocument={currentDocumentId}
              fileMetadata={fileMetadata}
              setFileMetadata={setFileMetadata}
              isEdit={isEdit}
              handleCanvasPageEvent={handleCanvasPageEvent}
              loadNewUrlImage={loadNewUrlImage}
              isCreateContract={isCreateContract}
            />
          </DocumentsChangeWrapper>
        )}
        <CustomContractWrapper
          isMobile={isMobile}
          isSigning={isSigning}
          onDragOver={e => {
            e.preventDefault()
          }}
          onDrop={e => {
            const stage = stageRef?.current?.getStage()
            const layer = stage.find('Layer')
            let currentPageName
            layer[0].find('Image')?.forEach(item => {
              if (item.visible() === true) {
                currentPageName = item.id()
              }
            })

            const transferredData = JSON.parse(e.dataTransfer.getData('text'))

            stageRef.current.setPointersPositions(e)
            const { x, y } = stageRef.current.getPointerPosition()
            const fileId = currentDocument?.id

            const oldItems = path([fileId], items) ?? []

            const updatedItems = updateAt(
              items,
              [fileId],
              [
                ...oldItems,
                {
                  x: x,
                  y: y,
                  width: 200,
                  height: transferredData.inputType === 'text' ? 16 : 48,
                  stroke: transferredData.color,
                  name: currentPageName,
                  id: `${transferredData.id}_${camelCase(transferredData.text)}_${Math.floor(
                    Math.random() * 10000
                  )}`,
                  recipient_id: transferredData.recipient_id,
                  text: transferredData.text,
                  inputType: transferredData.inputType,
                  textSize: 16
                }
              ]
            )

            setItems(updatedItems)
          }}
        >
          <CustomContractViewWrapper
            style={{
              width: STAGE_WIDTH,
              height: VIEWPORT_HEIGHT,
              overflow: 'scroll auto',
              border: '1px solid #ddd',
              margin: '10px auto'
            }}
            isMobile={isMobile}
          >
            <Stage
              ref={stageRef}
              width={imageDimensions?.resizedWidth ?? STAGE_WIDTH}
              height={imageDimensions?.resizedHeight ?? STAGE_HEIGHT}
              x={stage.x}
              y={stage.y}
              onDragOver={e => {
                e.preventDefault()
              }}
              onClick={onClickHandler}
              draggable={isMobile}
              onTouchMove={handleTouch}
              onTouchEnd={handleTouchEnd}
              onDragStart={e => {
                if (isMobile && e.target.scaleX() === 1) e.target.stopDrag()
              }}
            >
              {isEmpty(imageDimensions) ? (
                <Layer>
                  {/* HTML is used to create DOM components inside Konva stage. https://github.com/konvajs/react-konva-utils */}
                  <Html>
                    <LoadingWrapper width={STAGE_WIDTH} height={STAGE_HEIGHT}>
                      <LoadingSpinner width={50} height={50} />
                    </LoadingWrapper>
                  </Html>
                </Layer>
              ) : (
                <Layer>
                  {urls?.map((url, i) => {
                    return <ContractImage key={i} url={url} i={i} dimensions={imageDimensions} />
                  })}

                  {/* Existing initial and signatures */}
                  {existingSignatures !== null &&
                    Object.values(existingSignatures).map(item => displaySignatures(item.value))}

                  {/* Current user signature */}
                  {signedItems.taps && displaySignatures(signedItems)}

                  {/* All of text fields */}
                  {existingTextFields !== null &&
                    displayTextFields(existingTextFields, selectedPage)}

                  {!hasSigned &&
                    currentItems.map((item, i) => {
                      return (
                        <InputWidget
                          key={i}
                          shapeProps={item}
                          text={item.text}
                          isSelected={item.id === selectedShape}
                          onSelect={() => {
                            selectShape(item.id)
                            if (isSigning) {
                              const date = moment().format('YYYY-MM-DD HH:mm:ss')
                              const newSignedItemsTaps = signedItems.taps.filter(object => {
                                return object.itemId !== item.id
                              })
                              const type = item.text.split(' ')[0]
                              const updateItems = e => {
                                const displayRatio = (STAGE_WIDTH / imageDimensions.width) * 10
                                const resolution = currentDocument.location[1][0].x
                                // these ratio values are based on the height / width of the captured image
                                // .23 is the ratio for captured text
                                // .9244 is the ratio for initial draw image
                                // .3986 is the ratio for signature draw image
                                const ratio =
                                  e?.type === 'text' ? 0.23 : !!e.initial ? 0.9244 : 0.3986
                                const newWidth = item.height / ratio
                                //offset from the resize to place item in expected position
                                const newX = item.x + (item.width - newWidth) / 2
                                setSignedItems({
                                  initial: e.initial ? e.initial : signedItems.initial,
                                  signature: e.signature ? e.signature : signedItems.signature,
                                  taps: [
                                    ...newSignedItemsTaps,
                                    {
                                      itemId: item.id,
                                      date: date,
                                      type: type,
                                      width: newWidth,
                                      height: item.height,
                                      x: newX,
                                      y: item.y,
                                      name: item.name,
                                      pdf_width: getPdfValue(displayRatio, resolution, item.width),
                                      pdf_height: getPdfValue(
                                        displayRatio,
                                        resolution,
                                        item.height
                                      ),
                                      pdf_x: getPdfValue(displayRatio, resolution, item.x),
                                      pdf_y: getPdfValue(displayRatio, resolution, item.y),
                                      signer_id: parseInt(fileMetadata.current_signer_id)
                                    }
                                  ]
                                })
                                setItems(
                                  updateAt(
                                    items,
                                    [currentDocumentId],
                                    currentItems.filter(
                                      existingItems => existingItems.id !== item.id
                                    )
                                  )
                                )
                              }
                              if (type === 'Initials') {
                                if (signedItems.initial === null) {
                                  showInitialModal({
                                    onSave: e => {
                                      updateItems(e)
                                    }
                                  })
                                } else {
                                  updateItems({ initial: signedItems.initial })
                                }
                              } else {
                                if (signedItems.signature === null) {
                                  showSignatureModal({
                                    onSave: e => {
                                      updateItems(e)
                                    }
                                  })
                                } else {
                                  updateItems({ signature: signedItems.signature })
                                }
                              }
                              return
                            }
                            // selectShape(item.id)
                          }}
                          onChange={newAttrs => {
                            const newItems = currentItems.slice()
                            newItems[i] = newAttrs

                            setItems(updateAt(items, [currentDocumentId], newItems))
                          }}
                          parentRef={stageRef}
                          id={item.id}
                          name={item.name}
                          currentItems={currentItems}
                          selectShape={selectShape}
                          handleDuplicate={handleDuplicate}
                          handleDelete={handleDelete}
                          currentStep={currentStep}
                          isSigning={isSigning}
                          isMobile={isMobile}
                          inputType={item.inputType}
                          currentDocument={currentDocumentId}
                          contractId={contractId}
                          signees={signees}
                          setSignees={setSignees}
                        />
                      )
                    })}
                </Layer>
              )}
            </Stage>
          </CustomContractViewWrapper>

          <CustomContractControllerWrapper
            style={{
              width: STAGE_WIDTH,
              height: '32px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
            isMobile={isMobile}
          >
            {isSigning ? (
              <>
                <DocumentNameWrapper>{currentDocument?.file_original_name}</DocumentNameWrapper>
                <div>
                  <LeftChevron onClick={() => selectPage('right')} disabled={prevPageDisabled}>
                    <Icon
                      id={prevPageDisabled ? 'chevron_left_disabled' : 'chevron_left_enabled'}
                      height="8px"
                      width="5px"
                    />
                  </LeftChevron>
                  <CounterWrapper>
                    {selectedPage + 1} / {urls.length}
                  </CounterWrapper>
                  <RightChevron onClick={() => selectPage('left')} disabled={nextPageDisabled}>
                    <Icon
                      id={nextPageDisabled ? 'chevron_right_disabled' : 'chevron_right_enabled'}
                      height="8px"
                      width="5px"
                    />
                  </RightChevron>
                </div>
              </>
            ) : (
              <PageButtons
                urls={urls}
                index={selectedPage}
                selectPage={selectPage}
                previousDocument={currentIndex !== 0}
                nextDocument={currentIndex !== formattedFiles?.length - 1}
                isEdit={isEdit}
              />
            )}
          </CustomContractControllerWrapper>
        </CustomContractWrapper>
        {!isSigning && (
          <DocumentSettingsWrapper className="nonCanvas" isMobile={isMobile}>
            <DocumentWidget
              urls={urls}
              stageRef={stageRef}
              selectedPage={selectedPage}
              setSelectedPage={setSelectedPage}
              updateSelectedPage={updateSelectedPage}
              zoomLevel={zoomLevel}
              handleClick={handleZoom}
              handleDuplicate={handleDuplicate}
              handleDelete={handleDelete}
              selectedShape={selectedShape}
              currentPage={currentStep}
              signees={signees}
              setSignees={setSignees}
              currentDocument={currentDocumentId}
              contractId={contractId}
              stageWidth={STAGE_WIDTH}
              stageHeight={STAGE_HEIGHT}
              isSigning={isSigning}
              selectShape={selectShape}
              parentRef={stageRef}
            />
          </DocumentSettingsWrapper>
        )}
      </Container>
      {isCreateContract && (
        <CreateContractButton
          isLastPage={isLastPage}
          isEdit={isEdit}
          fileMetadata={fileMetadata}
          setFileMetadata={setFileMetadata}
          formattedFiles={formattedFiles}
          contractId={contractId}
          currentIndex={currentIndex}
          pages={pages}
          currentDocument={currentDocument}
          setSelectedPage={setSelectedPage}
          handleBack={handleBack}
        />
      )}
    </>
  )
}

export default DocumentView
